import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Cloud,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  Trash2,
  ChevronDown,
  Code,
  PlayCircle,
  Plus,
  RefreshCw,
} from "lucide-react";
import oneDriveService, {
  OneDriveCredential,
  OneDriveFolder,
} from "../../services/oneDrive.service";
import syntaxHighlight from "../../utils/jsonViewer";
import NodeDocsButton from "../../docs/NodeDocsButton";

export const OneDriveTriggerNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const { setNodes, deleteElements } = useReactFlow();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
  const [hasLoadedFolders, setHasLoadedFolders] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const authPopupRef = useRef<Window | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(
    data?.credential_id || "",
  );
  const [folderId, setFolderId] = useState<string>(data?.folder_id || "");
  const [credentials, setCredentials] = useState<OneDriveCredential[]>([]);
  const [folders, setFolders] = useState<OneDriveFolder[]>([]);

  const stopPropagation = useCallback((e: any) => e.stopPropagation(), []);

  // Listen for auth success message from popup
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const allowed = [
        "http://localhost:3000",
        "http://localhost:5173",
        "https://nightly-dms.skill-mine.com",
        "https://demo-dms.skill-mine.com",
      ];

      if (!allowed.includes(event.origin)) return;

      if (
        event.data?.type === "ONEDRIVE_AUTH_SUCCESS" ||
        event.data?.status === "success"
      ) {
        setConnecting(false);
        // Refresh list
        loadCredentials();

        const popup = authPopupRef.current;
        if (popup && !popup.closed) popup.close();
      }
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  // Poll for popup closure to refresh list
  useEffect(() => {
    if (!connecting) return;

    const pollInterval = setInterval(() => {
      const popup = authPopupRef.current;

      if (popup && popup.closed) {
        authPopupRef.current = null;
        setConnecting(false);
        // Refresh credentials when popup closes, assuming user might have added one
        loadCredentials();
        clearInterval(pollInterval);
      }
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [connecting]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as any)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openAuthPopup = async () => {
    try {
      setConnecting(true);
      setErrorMsg(null);

      const templateId =
        data?.template_id ||
        data?.request_id ||
        localStorage.getItem("template_id");

      console.log("🔑 Opening OneDrive auth with template_id:", templateId);

      const authUrl = oneDriveService.getOneDriveLoginUrl(templateId);
      console.log("🌐 Auth URL:", authUrl);

      const width = 500;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        "OneDriveAuthPopup",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
      );

      if (!popup) {
        throw new Error("Popup blocked! Please allow popups for this site.");
      }

      authPopupRef.current = popup;
      popup.focus();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to open login popup");
      setConnecting(false);
    }
  };

  const loadCredentials = async () => {
    const templateId = data?.template_id || localStorage.getItem("template_id");

    try {
      setErrorMsg(null);
      const response = await oneDriveService.listCredentials(templateId);
      if (response && response.success) {
        setCredentials(response.data || []);
      } else {
        setErrorMsg("Failed to load credentials");
      }
      setHasLoadedCredentials(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load credentials");
    }
  };

  const loadFolders = async () => {
    if (loadingFolders || !selectedCredentialId) return;

    try {
      setLoadingFolders(true);
      setErrorMsg(null);

      const templateId =
        data?.template_id || localStorage.getItem("template_id");
      const response = await oneDriveService.listFolders(
        selectedCredentialId,
        templateId,
      );

      if (response && response.success && Array.isArray(response.data)) {
        setFolders(response.data);
      } else {
        setErrorMsg("Failed to load folders");
      }
      setHasLoadedFolders(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load folders");
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;

    if (!hasLoadedCredentials) {
      loadCredentials();
    }
    if (selectedCredentialId && !hasLoadedFolders) {
      loadFolders();
    }
  }, [
    isModalOpen,
    hasLoadedCredentials,
    hasLoadedFolders,
    selectedCredentialId,
  ]);

  useEffect(() => {
    if (selectedCredentialId) {
      setHasLoadedFolders(false);
    } else {
      setFolders([]);
      setFolderId("");
    }
  }, [selectedCredentialId]);

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                credential_id: selectedCredentialId,
                folder_id: folderId,
              },
            }
          : n,
      ),
    );
  }, [selectedCredentialId, folderId, id, setNodes]);

  const validationStatus = useMemo(() => {
    const errors: string[] = [];
    if (!selectedCredentialId) errors.push("OneDrive credential required");
    if (!folderId) errors.push("Folder selection is required");

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [selectedCredentialId, folderId]);

  const hasConfig = validationStatus.isValid;
  const hasExecutionOutputs =
    data?.outputs && Object.keys(data.outputs).length > 0;

  const statusConfig = useMemo(() => {
    return hasConfig
      ? { bg: "bg-emerald-500", text: "Configured", pulse: false }
      : { bg: "bg-gray-400", text: "Not Configured", pulse: false };
  }, [hasConfig]);

  const actualOutputsJson = useMemo(() => {
    if (hasExecutionOutputs) {
      return JSON.stringify(data.outputs, null, 2);
    }
    return "";
  }, [data?.outputs, hasExecutionOutputs]);

  const highlightedActualOutputs = useMemo(() => {
    if (actualOutputsJson) {
      return syntaxHighlight(actualOutputsJson);
    }
    return "";
  }, [actualOutputsJson]);

  return (
    <>
      <div
        className={`relative group bg-blue-50 border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
          !validationStatus.isValid
            ? "border-red-500 shadow-red-200"
            : selected
              ? "border-blue-500 shadow-md"
              : "border-blue-300 hover:border-blue-400"
        }`}
        onClick={stopPropagation}
      >
        {/* Execute Button */}
        <div className="absolute -top-3 -right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onExecuteNode?.(id);
            }}
            className="p-1.5 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all"
            title="Execute to this node"
          >
            {/* Note: OneDriveTriggerNode might not have isExecuting state logic fully hooked up in the same way, 
                but we use data.executionStatus if available. 
                The component doesn't explicitly destructure isExecuting from data in the same way as others,
                but we can check data.executionStatus directly or use the props if we added them.
                Wait, I see `const executionStatus = data?.executionStatus || null;` is NOT in OneDriveTriggerNode.
                I should add that logic too.
            */}
            {data?.executionStatus === "running" ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <PlayCircle className="w-3 h-3" />
            )}
          </button>
        </div>
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-blue-600 border-2 border-white"
          style={{
            boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.25)",
          }}
        />

        <div className="flex items-start justify-between mb-3 cursor-move">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-blue-100">
              <Cloud className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-800 text-sm">
                OneDrive Trigger
              </h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                    statusConfig.pulse ? "animate-pulse" : ""
                  }`}
                />
                <p className="text-xs text-gray-600">{statusConfig.text}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NodeDocsButton nodeType="ONEDRIVE-CONNECTOR" />
            <button
              onClick={handleDeleteClick}
              className="text-red-500 hover:text-gray-700 p-1 rounded transition"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!validationStatus.isValid && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-900 mb-1">
                  Configuration Required
                </p>
                <ul className="text-red-700 space-y-0.5">
                  {validationStatus.errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3" onClick={stopPropagation}>
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-blue-700 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" /> Configure Trigger
          </button>
        </div>

        {selectedCredentialId && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="font-medium text-gray-700">Credential:</span>
              <span className="truncate max-w-[120px]">
                {credentials.find((c) => c._id === selectedCredentialId)
                  ?.user_info.user_email || "Selected"}
              </span>
            </div>
          </div>
        )}
      </div>

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded">
                    <Cloud className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure OneDrive Trigger
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Authenticate with OneDrive and configure trigger settings
                    </p>
                  </div>
                </div>
                <button
                  className="text-gray-400 p-1"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-slate-200">
                <div className="flex-1 overflow-y-auto bg-slate-50/30 p-8 border-l-4 border-l-blue-500/20">
                  {!validationStatus.isValid && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Configuration Issues
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                            {validationStatus.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                      Trigger Configuration
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Setup your OneDrive connection and monitoring folder
                    </p>
                  </div>

                  <div className="space-y-5">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Credentials
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Select which OneDrive account to use for this trigger.
                      </p>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Credential
                        </label>
                        <div className="relative" ref={dropdownRef}>
                          <div
                            onClick={() => {
                              if (!isDropdownOpen) {
                                loadCredentials();
                              }
                              setIsDropdownOpen(!isDropdownOpen);
                            }}
                            className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-blue-400 focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer flex items-center justify-between shadow-sm"
                          >
                            <span
                              className={`truncate ${
                                selectedCredentialId
                                  ? "text-gray-900 font-medium"
                                  : "text-gray-400"
                              }`}
                            >
                              {selectedCredentialId
                                ? credentials.find(
                                    (c) => c._id === selectedCredentialId,
                                  )
                                  ? `${
                                      credentials.find(
                                        (c) => c._id === selectedCredentialId,
                                      )?.user_info.user_name
                                    } (${
                                      credentials.find(
                                        (c) => c._id === selectedCredentialId,
                                      )?.user_info.user_email
                                    })`
                                  : "Credential ID Selected"
                                : "Select a credential..."}
                            </span>
                            <ChevronDown
                              className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${
                                isDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>

                          {isDropdownOpen && (
                            <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg border border-gray-100 py-1 text-sm animate-in fade-in zoom-in-95 duration-100">
                              <div className="max-h-60 overflow-y-auto">
                                {credentials.length === 0 && (
                                  <div className="px-3 py-2 text-gray-400 text-xs italic">
                                    No credentials found
                                  </div>
                                )}
                                {credentials.map((cred) => (
                                  <div
                                    key={cred._id}
                                    onClick={() => {
                                      setSelectedCredentialId(cred._id);
                                      setIsDropdownOpen(false);
                                    }}
                                    className={`px-3 py-2 cursor-pointer flex items-center justify-between group transition-colors ${
                                      selectedCredentialId === cred._id
                                        ? "bg-blue-50 text-blue-700"
                                        : "hover:bg-gray-50 text-gray-700"
                                    }`}
                                  >
                                    <div className="truncate">
                                      <div className="font-medium">
                                        {cred.user_info.user_name}
                                      </div>
                                      <div className="text-xs text-gray-500 group-hover:text-gray-600">
                                        {cred.user_info.user_email}
                                      </div>
                                    </div>
                                    {selectedCredentialId === cred._id && (
                                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="border-t border-gray-100 mt-1 pt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAuthPopup();
                                    setIsDropdownOpen(false);
                                  }}
                                  disabled={connecting}
                                  className="w-full text-left px-3 py-2 text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                                >
                                  {connecting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Plus className="w-4 h-4" />
                                  )}
                                  {connecting
                                    ? "Connecting..."
                                    : "Create new credential"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end mt-2">
                          <button
                            onClick={loadCredentials}
                            className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1 transition-colors"
                          >
                            <RefreshCw
                              className={`w-3 h-3 ${
                                connecting ? "animate-spin" : ""
                              }`}
                            />
                            Refresh List
                          </button>
                        </div>
                      </div>

                      {errorMsg && (
                        <p className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100 flex items-center gap-2">
                          <AlertTriangle className="w-3 h-3" />
                          {errorMsg}
                        </p>
                      )}
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Folder Selection
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Select the folder you want to monitor for new files.
                      </p>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          OneDrive Folder
                        </label>
                        <div className="flex gap-2">
                          <select
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={folderId}
                            onChange={(e) => setFolderId(e.target.value)}
                          >
                            <option value="">Select a folder...</option>
                            {folders.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}{" "}
                                {f.childCount > 0 ? `(${f.childCount})` : ""}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={loadFolders}
                            disabled={loadingFolders}
                            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            title="Refresh Folders"
                          >
                            <RefreshCw
                              className={`w-4 h-4 ${
                                loadingFolders ? "animate-spin" : ""
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-[450px] bg-white overflow-y-auto p-6 border-l border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                        Output
                      </h3>
                    </div>
                    {hasExecutionOutputs && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100">
                        {`${Object.keys(data.outputs).length} fields`}
                      </span>
                    )}
                  </div>

                  {hasExecutionOutputs ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                      <pre
                        className="whitespace-pre-wrap break-words"
                        dangerouslySetInnerHTML={{
                          __html: highlightedActualOutputs,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg text-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Code className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-xs font-medium text-gray-600">
                        No execution data
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        Run the workflow to see outputs
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm border rounded bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => setIsModalOpen(false)}
                    disabled={!validationStatus.isValid}
                    className={`px-4 py-2 text-sm rounded text-white ${
                      validationStatus.isValid
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default OneDriveTriggerNode;
